import {
  CSSProperties,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { GET_PLAYER_PROMOS } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { useDialogFocus } from 'hooks/useDialogFocus';
import { Promotion } from 'reducers/playerPromos';
import { SAFE_AREA_TOP } from 'utils/safeArea';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';

import { buildPromoDisplay } from './promoDisplay';
import { getPromoDetailsCopy, getPromoHybridDetailsCopy } from './promoMessaging';
import { getPromoVisualTheme, getTierProgressFill, PROMO_GOLD_METAL } from './promoTheme';

type PromoDetailsModalProps = {
  promo: Promotion;
  onClose: () => void;
};

const PromoDetailsModal = ({ promo, onClose }: PromoDetailsModalProps) => {
  const { socket } = useContext(SocketContext);
  const playerGUID = useAppSelector((state) => state.user?.playerGUID);
  const latestPromo = useAppSelector((state) =>
    state.playerPromos?.promotions?.find(
      (item: Promotion) =>
        (promo.promo_id && item.promo_id === promo.promo_id) ||
        (!promo.promo_id && item.promo_name === promo.promo_name)
    )
  );
  const resolvedPromo = latestPromo || promo;
  const fillRef = useRef<HTMLDivElement>(null);
  const animatedPromoRef = useRef<string | number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [isClosing, setIsClosing] = useState(false);
  const display = buildPromoDisplay(resolvedPromo);
  const isHybridDetails = display.format === 'hybrid_freeroll_qualification';
  const detailsThemePreset = getDetailsThemePreset(
    resolvedPromo.player_facing_config?.theme,
    display.format
  );
  const theme = getPromoVisualTheme(display.format, detailsThemePreset);
  const selectedTheme = getPromoVisualTheme(
    display.format,
    resolvedPromo.player_facing_config?.theme
  );
  const promoKey = resolvedPromo.promo_id || resolvedPromo.promo_name;
  const remaining = Math.max(display.targetValue - display.progressValue, 0);
  const remainingLabel =
    display.remainingLabel ||
    (display.format === 'weekly_timeback_credit'
      ? `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : `${Number.isInteger(remaining) ? remaining.toFixed(0) : remaining.toFixed(1)}h`);
  const hasWeeklyMilestones = display.weekRows.length > 0;
  // Two-tier formats: reaching the top tier ($50) is dressed in the gold of the $100K promo.
  const isTieredFormat = display.tierState !== undefined;
  const topTierReached = display.tierState === 'bonusAchieved';
  // Cash back keeps the rewards up top and gathers the rules into one quiet block at the
  // bottom (demo 2026-10-26: "keep the good stuff showing as the good stuff").
  const isCashBack = display.format === 'weekly_cash_back';
  // The hours card's status keeps its one-line pill: where "$50 earned this week" does not fit, it reads
  // "$50 this week" (Jason, 10/29). The full text is measured off-screen against the card.
  const statusRef = useRef<HTMLDivElement>(null);
  const statusMeasureRef = useRef<HTMLSpanElement>(null);
  const [statusCompact, setStatusCompact] = useState(false);
  const shortStatusLabel =
    isCashBack && / earned this week$/.test(display.statusLabel)
      ? display.statusLabel.replace(/ earned this week$/, ' this week')
      : null;
  useLayoutEffect(() => {
    const badge = statusRef.current;
    const card = badge?.parentElement;
    if (!shortStatusLabel || !badge || !card) {
      setStatusCompact(false);
      return undefined;
    }
    const sideSpace = (element: Element, parts: string[]) => {
      const computed = window.getComputedStyle(element);
      return parts.reduce((sum, part) => sum + (parseFloat(computed.getPropertyValue(part)) || 0), 0);
    };
    const fit = () => {
      const available =
        card.clientWidth - sideSpace(card, ['padding-left', 'padding-right']);
      const needed =
        (statusMeasureRef.current?.offsetWidth ?? 0) +
        sideSpace(badge, ['padding-left', 'padding-right', 'border-left-width', 'border-right-width']);
      setStatusCompact(available > 0 && needed > available);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(card);
    // The full text can widen with the card unchanged, e.g. when the Outfit font finishes loading after
    // the first measure, so the off-screen copy is watched too.
    if (statusMeasureRef.current) observer.observe(statusMeasureRef.current);
    return () => observer.disconnect();
  }, [shortStatusLabel, display.statusLabel]);
  const tierSectionTitle =
    display.format === 'weekly_timeback_credit'
      ? 'Refund Tiers'
      : display.format === 'tournament_tier_qualification'
      ? 'Tournament Tiers'
      : display.format === 'weekly_cash_back'
      ? 'Cash Back Tiers'
      : hasWeeklyMilestones
      ? 'Reward Tiers'
      : 'Achievements';
  const copy = getPromoDetailsCopy(
    {
      promoName: resolvedPromo.promo_name,
      remainingLabel,
      display,
      playerFacingConfig: resolvedPromo.player_facing_config
    },
    tierSectionTitle
  );
  const hybridCopy = getPromoHybridDetailsCopy({
    promoName: resolvedPromo.promo_name,
    remainingLabel,
    display,
    playerFacingConfig: resolvedPromo.player_facing_config
  });
  const rewardSummaryValue = getRewardSummaryValue(display, copy.emptyTiersLabel);
  // Cash back hides the month's earnings until the first tier is earned; the hours card
  // then takes the full width (demo 2026-10-27: no "Nothing yet" card).
  const showRewardSummary = !(display.monthTotalLabel !== undefined && !display.monthTotalLabel);
  const isWesternTheme = detailsThemePreset === 'western_orange_details';
  const tournamentDate =
    display.hybridDetails?.tournamentDateLabel ||
    display.detailRows.find((row) => row.label === 'Tournament Date')?.value ||
    '-';
  const stackLabels = getStackLabels(display);
  const finePrint = display.hybridDetails
    ? hybridCopy.termsAndConditions
    : resolvedPromo.player_facing_config?.termsAndConditions || getFinePrint(display);
  const bonusWeekColumnCount = Math.max(display.weekRows.length, 1);
  const currentWeekCardTheme = getThemedDetailsCardStyles(selectedTheme);
  const isCurrentGoalComplete =
    display.targetValue > 0 && display.progressValue >= display.targetValue;
  const isCurrentGoalInProgress =
    display.targetValue > 0 && display.progressValue < display.targetValue;
  const westernRingProgressPct = isCurrentGoalComplete ? 100 : 100 - display.progressPct;
  const westernRingClassName = [
    'promo-western-ring',
    isCurrentGoalComplete ? 'is-complete' : isCurrentGoalInProgress ? 'is-spinning' : undefined
  ]
    .filter(Boolean)
    .join(' ');
  useEffect(() => {
    if (!fillRef.current) return;

    const width = `${display.progressPct}%`;
    if (animatedPromoRef.current === promoKey) {
      gsap.set(fillRef.current, { width });
      return;
    }

    animatedPromoRef.current = promoKey;
    gsap.fromTo(fillRef.current, { width: '0%' }, { width, duration: 0.8, ease: 'power2.out' });
  }, [display.progressPct, promoKey]);

  useEffect(() => {
    if (!socket) return;

    const fallbackPlayerGUID = localStorage.getItem('playerGUID') || '';
    const resolvedPlayerGUID = playerGUID || fallbackPlayerGUID;
    if (!resolvedPlayerGUID) return;

    socket.emit(GET_PLAYER_PROMOS, { playerGUID: resolvedPlayerGUID });
  }, [playerGUID, socket]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const handleClose = () => {
    if (isClosing) return;

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(onClose, 280);
  };
  // Focus moves into the panel, Tab stays inside, Escape closes, and focus returns on close.
  useDialogFocus(dialogRef, handleClose);
  const backdropClassName = [
    'promo-details-backdrop',
    isHybridDetails ? 'promo-details-backdrop--hybrid' : '',
    isClosing ? 'is-closing' : ''
  ]
    .filter(Boolean)
    .join(' ');
  const panelClassName = [
    'promo-details-panel',
    isHybridDetails ? 'promo-details-panel--hybrid' : '',
    isWesternTheme ? 'promo-details-panel--western' : '',
    isClosing ? 'is-closing' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    // Lock to portrait on mobile-landscape — this modal portals to <body>,
    // outside the lobby wrapper, so the rotation lives inside the portal.
    <ForcePortrait>
      <div
        className={backdropClassName}
        data-testid='promo-details-backdrop'
        style={modalBackdropStyle}
        onClick={handleClose}>
        <div
          ref={dialogRef}
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
          tabIndex={-1}
          className={panelClassName}
          data-testid='promo-details-panel'
          style={{
            ...modalStyle,
            borderColor: theme.borderColor,
            boxShadow: isWesternTheme
              ? '12px 0 32px rgba(0,0,0,0.6)'
              : `0 24px 80px rgba(0,0,0,0.48), ${theme.boxShadow}`,
            backgroundImage: isWesternTheme
              ? theme.background
              : `radial-gradient(circle at 16% 0%, ${theme.accentSoft}, transparent 34%), radial-gradient(circle at 92% 8%, ${theme.accentSoft}, transparent 28%), linear-gradient(180deg, #111827 0%, #080d18 100%)`,
            backgroundColor: '#080d18'
          }}
          onClick={(event) => event.stopPropagation()}>
          <style>{promoModalAnimationCss}</style>
          <span
            className='promo-modal-edge-glint promo-modal-edge-glint-top'
            style={theme.edgeGlintStyle}
            aria-hidden='true'
          />
          <span
            className='promo-modal-edge-glint promo-modal-edge-glint-right'
            style={theme.edgeGlintVerticalStyle}
            aria-hidden='true'
          />
          {modalSparkleClassNames.map((className) => (
            <span
              key={className}
              className={className}
              style={theme.sparkleStyle}
              aria-hidden='true'
            />
          ))}
          <div
            className='promo-details-header'
            style={{ ...panelHeaderStyle, borderBottomColor: theme.accentMuted }}>
            <button
              aria-label='Close'
              className='promo-details-close'
              style={{
                ...modalCloseStyle,
                borderColor: theme.accentMuted,
                color: theme.iconColor
              }}
              onClick={handleClose}>
              <CloseIcon style={closeIconStyle} />
            </button>
            {/* The promo icon sits bare: a second circle beside the close button read as two
                buttons (EN-11291). */}
            <span aria-hidden='true' style={modalIconWrapStyle}>
              <ConfirmationNumberIcon style={{ fontSize: '1.25rem', color: theme.iconColor }} />
            </span>
            <div style={headerCopyStyle}>
              <div
                id={titleId}
                className='promo-details-title'
                style={{
                  ...modalHeaderStyle,
                  color: isWesternTheme ? '#f1e3c7' : '#ffffff',
                  fontFamily: isHybridDetails
                    ? "'Oswald', sans-serif"
                    : modalHeaderStyle.fontFamily,
                  fontSize: isHybridDetails ? '1.06rem' : modalHeaderStyle.fontSize,
                  fontWeight: isHybridDetails ? 700 : modalHeaderStyle.fontWeight,
                  letterSpacing: isHybridDetails ? '0.04em' : modalHeaderStyle.letterSpacing
                }}>
                {isHybridDetails ? hybridCopy.headerTitle : copy.titleOverride}
              </div>
            </div>
          </div>

          <div
            className='promo-details-scroll'
            data-testid='promo-details-scroll'
            style={panelScrollStyle}>
            {isHybridDetails ? (
              <Fragment key='hybrid-dashboard-details'>
                <div className='promo-western-countdown' style={westernCountdownStyle}>
                  <svg
                    aria-label={`${display.metricLabel} progress`}
                    aria-valuemax={display.targetValue}
                    aria-valuemin={0}
                    aria-valuenow={Math.min(display.progressValue, display.targetValue)}
                    className={westernRingClassName}
                    role='progressbar'
                    viewBox='0 0 220 220'
                    style={westernRingStyle}>
                    <circle
                      className='promo-western-ring-track'
                      cx='110'
                      cy='110'
                      r='92'
                      fill='none'
                      strokeWidth='9'
                    />
                    <circle
                      className='promo-western-ring-fill'
                      cx='110'
                      cy='110'
                      r='92'
                      fill='none'
                      strokeWidth='9'
                      strokeDasharray='578.05'
                      strokeDashoffset={`${578.05 - 578.05 * (westernRingProgressPct / 100)}`}
                      strokeLinecap='round'
                      transform='rotate(-90 110 110)'
                    />
                  </svg>
                  <div style={westernCountdownInnerStyle}>
                    <div className='promo-western-countdown-num' style={westernCountdownNumStyle}>
                      {display.hybridDetails?.remainingLabel || remainingLabel}
                    </div>
                    <div style={westernCountdownSubStyle}>{hybridCopy.progressStatusMessage}</div>
                  </div>
                </div>

                <div
                  className='promo-western-card promo-western-card-themed'
                  data-testid='promo-current-week-card'
                  style={currentWeekCardTheme.card}>
                  {currentWeekCardTheme.frame && (
                    <span
                      aria-hidden='true'
                      data-testid='promo-current-week-card-frame'
                      style={currentWeekCardTheme.frame}
                    />
                  )}
                  <div style={westernCardHeadStyle}>
                    <span style={{ ...westernCardIconStyle, ...currentWeekCardTheme.icon }}>
                      <TrendingUpIcon style={westernCardIconSvgStyle} />
                    </span>
                    <span style={{ ...westernCardTitleStyle, ...currentWeekCardTheme.title }}>
                      Current Week's Hours
                    </span>
                    <span style={{ ...westernCardTitleSubStyle, ...currentWeekCardTheme.sub }}>
                      ({display.hybridDetails?.eligibleBlindsLabel || '$0.25 / $0.50+'} Blinds)
                    </span>
                  </div>
                  <div style={{ ...westernCardMetaStyle, ...currentWeekCardTheme.meta }}>
                    {display.hybridDetails?.weekWindowLabel || display.resetLabel}
                  </div>
                  <div style={{ ...westernCardNumberStyle, ...currentWeekCardTheme.number }}>
                    {display.hybridDetails?.currentWeekHoursLabel || display.progressLabel}
                  </div>
                </div>

                {hasWeeklyMilestones && (
                  <div style={westernBonusCardStyle}>
                    <div style={westernBonusTitleStyle}>Bonus Stack Progress</div>
                    <div
                      style={{
                        ...westernBonusRowStyle,
                        gridTemplateColumns: `repeat(${bonusWeekColumnCount}, minmax(0, 1fr))`
                      }}>
                      {display.weekRows.map((week, index) => {
                        const isCurrent = week.label.startsWith('Current Week');
                        const markState = week.reached ? 'done' : isCurrent ? 'current' : 'open';
                        return (
                          <div key={week.key} style={westernBonusWeekStyle}>
                            <div
                              style={{
                                ...westernBonusLabelStyle,
                                color: isCurrent ? '#f4a340' : 'rgba(231,217,181,0.72)'
                              }}>
                              WK{index + 1}
                            </div>
                            <div
                              style={{
                                ...westernBonusMarkStyle,
                                ...(markState === 'done' ? westernBonusMarkDoneStyle : {}),
                                ...(markState === 'current' ? westernBonusMarkCurrentStyle : {})
                              }}
                              aria-label={`Week ${index + 1} ${
                                markState === 'done'
                                  ? 'complete'
                                  : markState === 'current'
                                  ? 'in progress'
                                  : 'open'
                              }`}>
                              {markState === 'done' ? (
                                <CheckCircleIcon style={westernBonusMarkIconStyle} />
                              ) : markState === 'current' ? (
                                <AccessTimeIcon style={westernBonusMarkIconStyle} />
                              ) : null}
                            </div>
                            <div
                              style={{
                                ...westernBonusAmountStyle,
                                color:
                                  week.reached || isCurrent ? '#f1d3a1' : 'rgba(231,217,181,0.58)'
                              }}>
                              {week.rewardLabel}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={westernBonusNoteStyle}>{hybridCopy.bonusStackHint}</div>
                  </div>
                )}

                <div
                  className='promo-western-card promo-western-card-gold'
                  style={westernGoldCardStyle}>
                  <div style={{ ...westernCardHeadStyle, justifyContent: 'center' }}>
                    <span style={westernCardTitleStyle}>
                      Tournament Date:{' '}
                      <strong style={westernCardStrongStyle}>{tournamentDate}</strong>
                    </span>
                  </div>
                  <div style={westernDividerStyle} />
                  <div style={westernCardBodyStyle}>{hybridCopy.tournamentMessage}</div>
                </div>

                <div style={westernStackRowStyle}>
                  <div style={westernStackTileStyle}>
                    <div style={westernStackLabelStyle}>Starting</div>
                    <div style={westernStackAmountStyle}>{stackLabels.starting}</div>
                  </div>
                  <div style={westernStackTileStyle}>
                    <div style={westernStackLabelStyle}>Bonus</div>
                    <div style={westernStackAmountStyle}>{stackLabels.bonus}</div>
                  </div>
                </div>

                {display.hybridDetails?.totalFallbackAvailable && (
                  <div style={westernFallbackCardStyle}>
                    <div style={westernFallbackHeaderStyle}>
                      <span style={westernFallbackTitleStyle}>Last Chance Redemption</span>
                      <span style={westernFallbackStatusStyle}>
                        {display.hybridDetails.totalFallbackQualified ? 'Qualified' : 'Available'}
                      </span>
                    </div>
                    <div style={westernFallbackBodyStyle}>
                      Play {display.hybridDetails.totalTargetHoursLabel} within qualifying dates to
                      gain entry and {display.hybridDetails.totalFallbackStackLabel} starting stack.
                    </div>
                    <div style={westernFallbackMetaStyle}>
                      <span>Total Hours</span>
                      <strong>
                        {display.hybridDetails.totalHoursLabel} /{' '}
                        {display.hybridDetails.totalTargetHoursLabel}
                      </strong>
                    </div>
                  </div>
                )}

                <div style={westernFinePrintStyle}>{finePrint}</div>
              </Fragment>
            ) : (
              <div
                key='generic-promo-details'
                style={
                  showRewardSummary
                    ? detailsHeroGridStyle
                    : { ...detailsHeroGridStyle, gridTemplateColumns: 'minmax(0, 1fr)' }
                }>
                <div
                  style={{
                    ...modalProgressSummaryStyle,
                    backgroundImage: theme.background,
                    borderColor: theme.borderColor
                  }}>
                  <div>
                    <span style={modalMutedStyle}>{copy.metricLabel || display.metricLabel}</span>
                    <strong style={{ ...modalProgressValueStyle, ...tabularStyle }}>
                      {display.progressLabel}
                    </strong>
                  </div>
                  <div
                    ref={statusRef}
                    style={{
                      ...statusPillStyle,
                      ...(topTierReached
                        ? goldPillStyle
                        : {
                            backgroundColor: theme.actionBackground,
                            borderColor: theme.accentMuted,
                            color: theme.iconColor
                          })
                    }}>
                    {statusCompact && shortStatusLabel ? shortStatusLabel : display.statusLabel}
                    {shortStatusLabel && (
                      <span ref={statusMeasureRef} aria-hidden='true' style={statusMeasureStyle}>
                        {display.statusLabel}
                      </span>
                    )}
                  </div>
                  <div
                    role='progressbar'
                    aria-label={copy.metricLabel || display.metricLabel}
                    aria-valuemin={0}
                    aria-valuemax={display.targetValue}
                    aria-valuenow={Math.min(display.progressValue, display.targetValue)}
                    aria-valuetext={display.progressLabel}
                    style={{ ...tierTrackStyle, backgroundColor: theme.trackColor }}>
                    <div
                      ref={fillRef}
                      style={{
                        ...tierFillStyle,
                        backgroundImage: getTierProgressFill(theme, display.tierState)
                      }}
                    />
                  </div>
                </div>

                {showRewardSummary && (
                  <div
                    data-testid='promo-reward-summary'
                    className={topTierReached ? 'promo-gold-card' : undefined}
                    style={{
                      ...modalRewardSummaryStyle,
                      ...(topTierReached
                        ? goldCardStyle
                        : {
                            backgroundColor: theme.surfaceBackground,
                            borderColor: theme.accentMuted
                          })
                    }}>
                    <span
                      style={{
                        ...modalMutedStyle,
                        ...(topTierReached ? { color: GOLD_INK_SOFT } : {})
                      }}>
                      {copy.rewardSummaryLabel}
                    </span>
                    <strong
                      style={{
                        ...modalProgressValueStyle,
                        ...tabularStyle,
                        ...(topTierReached ? goldCardValueStyle : {})
                      }}>
                      {rewardSummaryValue}
                    </strong>
                  </div>
                )}
              </div>
            )}

            {!isHybridDetails && (
              <Fragment key='generic-details-sections'>
                {hasWeeklyMilestones && (
                  <div style={modalSectionStyle}>
                    <div className='promo-details-section-title' style={modalSectionTitleStyle}>
                      {copy.weeklyMilestonesTitle}
                    </div>
                    <div className='promo-week-panel' style={weekMilestonePanelStyle}>
                      <div
                        className={display.weekRows.length === 4 ? 'promo-week-grid--quad' : undefined}
                        style={
                          display.weekRows.length === 4
                            ? { ...weekMilestoneGridStyle, gridTemplateColumns: undefined }
                            : weekMilestoneGridStyle
                        }>
                        {display.weekRows.map((week) => {
                          const WeekIcon = week.reached
                            ? CheckCircleIcon
                            : RadioButtonUncheckedIcon;
                          const weekLabel = week.label.startsWith('Current Week: ')
                            ? `Week ${week.label.replace('Current Week: ', '')}`
                            : week.label;

                          return (
                            <div
                              key={week.key}
                              role='group'
                              aria-label={`${weekLabel}, ${week.status}${
                                week.isUpcoming ? '' : `, ${week.rewardLabel}`
                              }${week.playedLabel ? `, ${week.playedLabel} played` : ''}`}
                              style={{
                                ...weekMilestoneStyle,
                                ...(week.isCurrent
                                  ? { ...weekCurrentStyle, borderColor: theme.accentMuted }
                                  : {})
                              }}>
                              <span style={weekMilestoneLabelStyle}>{weekLabel}</span>
                              <WeekIcon
                                aria-hidden='true'
                                style={
                                  week.reached
                                    ? {
                                        ...weekReachedIconStyle,
                                        color: week.topTier ? GOLD_TEXT : theme.accentColor
                                      }
                                    : week.isUpcoming
                                    ? weekUpcomingIconStyle
                                    : weekOpenIconStyle
                                }
                              />
                              <strong
                                style={{
                                  ...tabularStyle,
                                  ...(week.reached
                                    ? {
                                        ...modalReachedStyle,
                                        color: week.topTier ? GOLD_TEXT : theme.accentColor
                                      }
                                    : week.isUpcoming
                                    ? weekUpcomingAmountStyle
                                    : modalNotReachedStyle)
                                }}>
                                {/* Same size as the amounts so all four cells line up. */}
                                {week.isUpcoming ? '\u2013' : week.rewardLabel}
                              </strong>
                              {week.rangeLabel && (
                                <span style={weekRangeStyle}>{week.rangeLabel}</span>
                              )}
                              {week.playedLabel && (
                                <span style={{ ...weekRangeStyle, ...tabularStyle }}>
                                  {week.playedLabel}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div style={weekMilestoneHintStyle}>{copy.weeklyMilestonesHint}</div>
                    </div>
                  </div>
                )}

                <div style={modalSectionStyle}>
                  <div
                    className='promo-details-section-title'
                    style={{ ...modalSectionTitleStyle, ...tierSectionTitleRowStyle }}>
                    <span>{copy.tierSectionTitle}</span>
                    {copy.tierValueColumnLabel && display.tiers.length > 0 && (
                      <span style={tierValueColumnLabelStyle}>{copy.tierValueColumnLabel}</span>
                    )}
                  </div>
                  {display.tiers.length > 0 ? (
                    display.tiers.map((tier, index) => {
                      const goldTier =
                        isTieredFormat && tier.reached && index === display.tiers.length - 1;
                      return (
                        <div
                          key={`${tier.threshold}-${tier.valueLabel}`}
                          style={{ ...modalRowStyle, ...(goldTier ? goldRowStyle : {}) }}>
                          <span
                            style={
                              tier.reached
                                ? {
                                    ...modalReachedStyle,
                                    color: goldTier ? GOLD_TEXT : theme.accentColor
                                  }
                                : modalNotReachedStyle
                            }>
                            {tier.reached ? copy.tierQualifiedLabel : copy.tierOpenLabel}
                          </span>
                          <span>{tier.label}</span>
                          <strong style={goldTier ? { color: GOLD_TEXT } : undefined}>
                            {tier.valueLabel}
                          </strong>
                        </div>
                      );
                    })
                  ) : (
                    <div style={modalEmptyStyle}>{copy.emptyTiersLabel}</div>
                  )}
                </div>

                {isCashBack ? (
                  <div data-testid='promo-fine-print' style={finePrintStyle}>
                    <dl style={finePrintListStyle}>
                      {display.detailRows.map((row) => (
                        <div key={row.label} style={finePrintRowStyle}>
                          <dt>{row.label}</dt>
                          <dd style={finePrintValueStyle}>{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                    {copy.finePrint && (
                      <p style={finePrintTextStyle}>{linkifyFinePrint(copy.finePrint)}</p>
                    )}
                    <p style={finePrintTextStyle}>
                      {[display.resetLabel, display.endLabel].filter(Boolean).join(' \u00b7 ')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={detailGridStyle}>
                      {display.detailRows.map((row) => (
                        <div key={row.label} style={detailItemStyle}>
                          <span>{row.label}</span>
                          <strong style={detailValueStyle}>{row.value}</strong>
                        </div>
                      ))}
                    </div>

                    <div style={endDateStyle}>
                      {[display.resetLabel, display.endLabel].filter(Boolean).join(' \u00b7 ')}
                    </div>
                  </>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </ForcePortrait>,
    document.body
  );
};

export default PromoDetailsModal;

const shouldUseEmptyRewardLabel = (earnedLabel: string): boolean =>
  ['not qualified', 'no timeback yet', 'none yet'].includes((earnedLabel || '').toLowerCase());

const getRewardSummaryValue = (
  display: ReturnType<typeof buildPromoDisplay>,
  emptyLabel: string
) => {
  // Two-tier formats show the month's total; the card's styling still follows this week.
  if (display.monthTotalLabel !== undefined) return display.monthTotalLabel || emptyLabel;
  if (shouldUseEmptyRewardLabel(display.earnedLabel) || !display.earnedLabel) return emptyLabel;
  if (display.format === 'hybrid_freeroll_qualification') {
    return `Tournament entry + ${display.earnedLabel}`;
  }

  return display.earnedLabel;
};

const getStackLabels = (display: ReturnType<typeof buildPromoDisplay>) => {
  if (display.hybridDetails) {
    return {
      starting: display.hybridDetails.startingStackLabel,
      bonus: display.hybridDetails.bonusStackLabel
    };
  }

  const weeklyTier = display.tiers.find((tier) => tier.label.includes('weekly'));
  const valueLabel = weeklyTier?.valueLabel || display.earnedLabel || '';
  const [starting = '0', bonus = '0'] = valueLabel.match(/\d+(?:\.\d+)?K?/gi) || [];

  return {
    starting,
    bonus
  };
};

const getFinePrint = (display: ReturnType<typeof buildPromoDisplay>) => {
  const details = display.detailRows.map((row) => `${row.label}: ${row.value}`).join('. ');
  const tiers = display.tiers.map((tier) => `${tier.label}: ${tier.valueLabel}`).join('. ');

  return [details, tiers].filter(Boolean).join('. ');
};

const modalSparkleClassNames = [
  'promo-modal-sparkle promo-modal-sparkle-one',
  'promo-modal-sparkle promo-modal-sparkle-two',
  'promo-modal-sparkle promo-modal-sparkle-three'
];

const getDetailsThemePreset = (themePreset: string | undefined, format: string) =>
  format === 'hybrid_freeroll_qualification' && themePreset === 'parchment'
    ? 'western_orange_details'
    : themePreset;

const getThemedDetailsCardStyles = (
  theme: ReturnType<typeof getPromoVisualTheme>
): Record<'card' | 'icon' | 'title' | 'sub' | 'meta' | 'number', CSSProperties> & {
  frame?: CSSProperties;
} => {
  const textShadow = theme.iconColor === '#ffffff' ? '0 1px 0 rgba(0,0,0,0.45)' : 'none';

  return {
    card: {
      ...westernGoldCardStyle,
      background: theme.background,
      backgroundImage: theme.background,
      borderColor: theme.borderColor,
      boxShadow: theme.boxShadow,
      color: theme.iconColor
    },
    icon: {
      color: theme.iconColor,
      textShadow
    },
    title: {
      color: theme.iconColor,
      textShadow
    },
    sub: {
      color: theme.iconColor
    },
    meta: {
      color: theme.iconColor
    },
    number: {
      color: theme.iconColor,
      textShadow
    },
    frame: theme.detailsCardFrameStyle
  };
};

const promoModalAnimationCss = `
  @import url('https://fonts.googleapis.com/css2?family=Rye&family=Special+Elite&family=Oswald:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');

  /* Mobile browsers enlarge text on their own in narrow, tall layouts (Chrome's font boosting,
     Safari's text inflation), which pushed this panel's labels past their cards on very tall
     phones. Its sizes are set in px and rem, so it keeps them; the rest of the game is unchanged. */
  .promo-details-panel {
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  /* Four qualifying windows sit in one row when they fit, otherwise in a 2 x 2 grid, never 3 + 1
     (Jason, 10/29). 302px is four 68px windows and three 10px gaps. */
  .promo-week-panel {
    container: promo-weeks / inline-size;
  }

  .promo-week-grid--quad {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @container promo-weeks (max-width: 301.98px) {
    .promo-week-grid--quad {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .promo-details-backdrop {
    animation: promoDetailsBackdropIn 0.2s ease-out both;
  }

  .promo-details-backdrop.is-closing {
    animation: promoDetailsBackdropOut 0.28s ease-in both;
  }

  .promo-details-panel {
    animation: promoDetailsSlideIn 0.32s cubic-bezier(0.4, 0, 0.2, 1) both;
  }

  .promo-details-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    pointer-events: none;
    z-index: 0;
  }

  .promo-details-panel.is-closing {
    animation: promoDetailsSlideOut 0.28s cubic-bezier(0.4, 0, 0.2, 1) both;
  }

  .promo-details-scroll::-webkit-scrollbar {
    width: 6px;
  }

  .promo-details-scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  .promo-details-scroll::-webkit-scrollbar-thumb {
    background: rgba(233, 140, 38, 0.3);
    border-radius: 999px;
  }

  .promo-gold-card {
    overflow: hidden;
    isolation: isolate;
  }

  .promo-gold-card::after {
    content: "";
    position: absolute;
    top: -20%;
    bottom: -20%;
    left: 0;
    width: 45%;
    background: linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.65) 50%, transparent 80%);
    mix-blend-mode: screen;
    pointer-events: none;
    transform: translateX(-120%);
    animation: promoGoldSheen 1.1s cubic-bezier(0.23, 1, 0.32, 1) 0.35s 1 both;
  }

  @keyframes promoGoldSheen {
    from { transform: translateX(-120%); }
    to { transform: translateX(260%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .promo-gold-card::after {
      animation: none;
      opacity: 0;
    }
  }

  .promo-details-close {
    transition: transform 120ms cubic-bezier(0.2, 0, 0, 1);
  }

  .promo-details-close:active {
    transform: scale(0.96);
  }

  .promo-details-close:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  .promo-details-title {
    font-family: 'Rye', serif;
    letter-spacing: 0.02em;
    text-shadow: 0 1px 0 rgba(0,0,0,0.55);
  }

  .promo-details-subtitle,
  .promo-details-section-title {
    font-family: 'Special Elite', monospace;
    letter-spacing: 0.14em;
  }

  .promo-details-panel--hybrid .promo-modal-edge-glint,
  .promo-details-panel--hybrid .promo-modal-sparkle {
    display: none;
  }

  .promo-details-panel--western {
    width: min(560px, 100vw) !important;
    max-width: min(560px, 100vw) !important;
  }

  .promo-western-ring-track {
    stroke: rgba(232,130,31,0.15);
  }

  .promo-western-ring-fill {
    stroke: #e8821f;
    filter: drop-shadow(0 0 6px rgba(232,130,31,0.6)) drop-shadow(0 0 18px rgba(232,130,31,0.35));
  }

  .promo-western-ring.is-spinning {
    animation: promoWesternRingSpin 2.4s linear infinite;
    transform-box: fill-box;
    transform-origin: center;
    overflow: visible;
  }

  .promo-western-ring.is-complete {
    animation: none;
  }

  .promo-western-card-gold::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    mix-blend-mode: multiply;
    opacity: 0.4;
  }

  .promo-hybrid-hero {
    isolation: isolate;
  }

  .promo-hybrid-hero > *,
  .promo-hybrid-week-panel > *,
  .promo-hybrid-row > *,
  .promo-hybrid-detail > * {
    position: relative;
    z-index: 1;
  }

  .promo-hybrid-title,
  .promo-hybrid-progress-row strong,
  .promo-hybrid-reward strong,
  .promo-hybrid-row,
  .promo-hybrid-detail strong {
    font-family: 'Oswald', sans-serif;
  }

  .promo-hybrid-stamp,
  .promo-hybrid-progress-row span,
  .promo-hybrid-reward span,
  .promo-hybrid-week,
  .promo-hybrid-detail span {
    font-family: 'Special Elite', monospace;
  }

  .promo-modal-edge-glint {
    position: absolute;
    border-radius: 999px;
    opacity: 0;
    pointer-events: none;
    z-index: 1;
  }

  .promo-modal-edge-glint-top {
    top: 0;
    left: 18px;
    width: min(120px, 28%);
    height: 2px;
    animation: promoModalGlintTop 5.6s ease-in-out infinite;
  }

  .promo-modal-edge-glint-right {
    top: 18px;
    right: 0;
    width: 2px;
    height: 48px;
    animation: promoModalGlintRight 5.6s ease-in-out 0.75s infinite;
  }

  .promo-modal-sparkle {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
    z-index: 1;
  }

  .promo-modal-sparkle-one {
    top: 24px;
    left: 24%;
    animation: promoModalSparkle 5.8s ease-in-out 0.8s infinite;
  }

  .promo-modal-sparkle-two {
    top: 76px;
    right: 18%;
    animation: promoModalSparkle 6.4s ease-in-out 2.4s infinite;
  }

  .promo-modal-sparkle-three {
    bottom: 22px;
    left: 17%;
    animation: promoModalSparkle 7s ease-in-out 3.2s infinite;
  }

  @keyframes promoModalGlintTop {
    0%, 42%, 100% {
      opacity: 0;
      transform: translateX(0);
    }
    10% {
      opacity: 1;
    }
    28% {
      opacity: 0.85;
      transform: translateX(calc(min(560px, 100vw - 36px) - 156px));
    }
    38% {
      opacity: 0;
      transform: translateX(calc(min(560px, 100vw - 36px) - 156px));
    }
  }

  @keyframes promoModalGlintRight {
    0%, 42%, 100% {
      opacity: 0;
      transform: translateY(0);
    }
    10% {
      opacity: 0.9;
    }
    28% {
      opacity: 0.8;
      transform: translateY(min(132px, calc(100vh - 120px)));
    }
    38% {
      opacity: 0;
      transform: translateY(min(132px, calc(100vh - 120px)));
    }
  }

  @keyframes promoModalSparkle {
    0%, 62%, 100% {
      opacity: 0;
      transform: scale(0.45);
    }
    70% {
      opacity: 0.9;
      transform: scale(1);
    }
    78% {
      opacity: 0.25;
      transform: scale(0.72);
    }
  }

  @keyframes promoWesternRingSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes promoDetailsSlideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes promoDetailsSlideOut {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }

  @keyframes promoDetailsBackdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes promoDetailsBackdropOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .promo-details-backdrop,
    .promo-details-panel {
      animation: none;
    }

    .promo-modal-edge-glint,
    .promo-modal-sparkle {
      animation: none;
      opacity: 0;
    }

    .promo-western-ring.is-spinning {
      animation: none;
    }
  }

  /* Touch devices (any orientation) get a full-width panel. Keyed on pointer
     capability rather than viewport width: a phone in landscape is ~932px wide
     but is force-rotated to portrait, where the panel must still fill its box.
     A width query (max-width:640px) never matches that landscape width and left
     the panel capped at its 390px desktop width → edge content (last week
     marker, bonus card) clipped. 100% is container-relative, so it fills the
     ForcePortrait box when rotated and the viewport when not. */
  @media (hover: none) {
    .promo-details-panel {
      width: 100% !important;
      max-width: 100% !important;
    }

    .promo-details-scroll {
      padding-left: 14px !important;
      padding-right: 14px !important;
    }
  }
`;

const modalBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  overflow: 'hidden',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch'
};

const modalStyle: CSSProperties = {
  position: 'absolute',
  // The panel takes focus itself on open; its controls keep their own focus rings.
  outline: 'none',
  top: 0,
  left: 0,
  bottom: 0,
  width: 'min(390px, 100%)',
  maxWidth: 'min(390px, 100%)',
  // Size against the containing block (100%), NOT the device viewport (100dvh).
  // When ForcePortrait rotates the modal on mobile-landscape, its box is
  // 100vh wide × 100vw tall; viewport units resolve to the un-rotated device
  // and leave the panel short → content cropped. 100% fills the rotated box.
  height: '100%',
  maxHeight: '100%',
  backgroundColor: 'rgba(13, 19, 31, 0.98)',
  background:
    'radial-gradient(ellipse 620px 420px at 28% 0%, rgba(232,130,31,0.08), transparent 60%), linear-gradient(180deg, #111827 0%, #080d18 100%)',
  border: '1px solid rgba(232, 130, 31, 0.3)',
  borderTop: 0,
  borderBottom: 0,
  borderLeft: 0,
  borderRadius: 0,
  boxShadow: '12px 0 32px rgba(0,0,0,0.6)',
  color: '#fff',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 0
};

const panelHeaderStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
  // One solid hairline (tinted by the theme) separates the header from the scroll area.
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  flexShrink: 0,
  // The panel is full-bleed (top:0), so on notched/status-bar devices the top
  // row would sit under the clock and the close/minimize buttons become
  // untappable. Add the safe-area inset (0 on web/desktop — no-op there).
  paddingTop: `calc(18px + ${SAFE_AREA_TOP})`,
  paddingRight: 16,
  paddingBottom: 16,
  paddingLeft: 16
};

const headerCopyStyle: CSSProperties = {
  minWidth: 0,
  flex: 1
};

const panelScrollStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 16px 24px'
};

const modalCloseStyle: CSSProperties = {
  width: 36,
  height: 36,
  flexShrink: 0,
  border: '1px solid rgba(232, 130, 31, 0.3)',
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'rgba(231, 217, 181, 0.78)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0
};

const closeIconStyle: CSSProperties = {
  fontSize: '1rem'
};

// Wraps to a second, balanced line instead of truncating the promo name on phones.
const modalHeaderStyle: CSSProperties = {
  color: '#ffffff',
  fontSize: '1.1rem',
  fontWeight: 400,
  lineHeight: 1.2,
  textWrap: 'balance' as CSSProperties['textWrap'],
  overflowWrap: 'break-word'
};

const modalIconWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const detailsHeroGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.6fr) minmax(150px, 0.9fr)',
  gap: '10px'
};

const westernCountdownStyle: CSSProperties = {
  aspectRatio: '1 / 1',
  margin: '0 auto 8px',
  position: 'relative',
  width: 160
};

const westernRingStyle: CSSProperties = {
  display: 'block',
  width: '100%'
};

const westernCountdownInnerStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  inset: 0,
  justifyContent: 'center',
  pointerEvents: 'none',
  position: 'absolute'
};

const westernCountdownNumStyle: CSSProperties = {
  color: '#f4a340',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1.65rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  lineHeight: 1,
  textShadow: '0 0 10px rgba(232,130,31,0.5), 0 2px 0 rgba(0,0,0,0.5)'
};

const westernCountdownSubStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Special Elite', monospace",
  fontSize: 'clamp(0.62rem, 2.3vw, 0.76rem)',
  letterSpacing: '0.1em',
  lineHeight: 1.2,
  marginTop: 4,
  maxWidth: 132,
  opacity: 0.85,
  overflowWrap: 'anywhere',
  textAlign: 'center',
  textTransform: 'uppercase'
};

const westernGoldCardStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #f3a04a 0%, #e07a1c 50%, #b85913 100%)',
  border: '1px solid #5c2a0a',
  borderRadius: 8,
  boxShadow:
    '0 0 0 1px rgba(232,130,31,0.4), 0 6px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,220,160,0.5)',
  color: '#f1d3a1',
  padding: '16px 20px 18px',
  position: 'relative'
};

const westernCardHeadStyle: CSSProperties = {
  alignItems: 'flex-start',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  position: 'relative',
  zIndex: 1
};

const westernCardIconStyle: CSSProperties = {
  color: '#f1d3a1',
  display: 'grid',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1.05rem',
  fontWeight: 800,
  placeItems: 'center'
};

const westernCardIconSvgStyle: CSSProperties = {
  fontSize: '1.12rem'
};

const westernCardTitleStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  lineHeight: 1.4,
  minWidth: 0,
  textShadow: '0 1px 0 rgba(0,0,0,0.45)',
  textTransform: 'uppercase'
};

const westernCardTitleSubStyle: CSSProperties = {
  color: '#f1d3a1',
  flexBasis: '100%',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  opacity: 0.92,
  paddingLeft: 26,
  position: 'relative',
  zIndex: 1
};

const westernCardMetaStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Special Elite', monospace",
  fontSize: '1rem',
  letterSpacing: '0.06em',
  lineHeight: 1.35,
  marginTop: 7,
  opacity: 0.95,
  position: 'relative',
  zIndex: 1
};

const westernCardNumberStyle: CSSProperties = {
  color: '#fff4d3',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '2rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  lineHeight: 1.05,
  marginTop: 8,
  position: 'relative',
  textShadow: '0 2px 0 rgba(0,0,0,0.45)',
  zIndex: 1
};

const westernCardStrongStyle: CSSProperties = {
  color: '#fff4d3',
  display: 'inline-block',
  fontFamily: "'Rye', serif",
  fontSize: '1.25rem',
  fontWeight: 400,
  letterSpacing: '0.02em',
  lineHeight: 1,
  marginLeft: 8,
  paddingBottom: 4,
  textShadow: '0 2px 0 rgba(0,0,0,0.5)',
  textTransform: 'none'
};

const westernDividerStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, transparent, rgba(255,235,200,0.5) 20%, rgba(255,235,200,0.5) 80%, transparent)',
  height: 1,
  margin: '10px 0',
  position: 'relative',
  zIndex: 1
};

const westernCardBodyStyle: CSSProperties = {
  color: '#fff4d3',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 600,
  letterSpacing: '0.03em',
  lineHeight: 1.45,
  padding: '4px 0 2px',
  position: 'relative',
  textAlign: 'center',
  textShadow: '0 1px 0 rgba(0,0,0,0.3)',
  zIndex: 1
};

const westernBonusCardStyle: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
  border: '1px solid rgba(232,130,31,0.35)',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  padding: '13px 16px',
  textAlign: 'center'
};

const westernBonusTitleStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.88rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  marginBottom: 12
};

const westernBonusRowStyle: CSSProperties = {
  display: 'grid',
  gap: 6,
  marginBottom: 10
};

const westernBonusWeekStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: 5
};

const westernBonusLabelStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '1rem',
  letterSpacing: '0.06em',
  opacity: 0.9,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const westernBonusMarkStyle: CSSProperties = {
  alignItems: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '50%',
  color: 'rgba(231,217,181,0.72)',
  display: 'flex',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1rem',
  fontWeight: 800,
  height: 32,
  justifyContent: 'center',
  width: 32
};

const westernBonusMarkIconStyle: CSSProperties = {
  fontSize: '1rem'
};

const westernBonusMarkDoneStyle: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(232,130,31,0.25), rgba(232,130,31,0.1))',
  borderColor: '#f4a340',
  color: '#f4a340'
};

const westernBonusMarkCurrentStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #f3a04a 0%, #e07a1c 100%)',
  borderColor: '#f3a04a',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 0 12px rgba(232,130,31,0.6)',
  color: '#1a0e06'
};

const westernBonusAmountStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.82rem',
  fontWeight: 700,
  letterSpacing: '0.02em'
};

const westernBonusNoteStyle: CSSProperties = {
  color: 'rgba(231,217,181,0.78)',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  lineHeight: 1.6,
  textAlign: 'center'
};

const westernStackRowStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
  gridTemplateColumns: '1fr 1fr'
};

const westernStackTileStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #e3cf9f 0%, #c2ad7a 100%)',
  border: '1px solid rgba(74,35,10,0.55)',
  borderRadius: 8,
  overflow: 'hidden',
  padding: '0 14px 12px',
  position: 'relative',
  textAlign: 'center'
};

const westernStackLabelStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #e07a1c 0%, #b85913 100%)',
  borderBottom: '1px solid rgba(74,35,10,0.55)',
  color: '#f1d3a1',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.18em',
  lineHeight: '26px',
  margin: '0 -14px',
  textShadow: '0 1px 0 rgba(0,0,0,0.4)',
  textTransform: 'uppercase'
};

const westernStackAmountStyle: CSSProperties = {
  color: '#1a0e06',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '2.1rem',
  fontWeight: 800,
  letterSpacing: '0.01em',
  lineHeight: 1,
  marginTop: 10,
  textShadow: '0 1px 0 rgba(255,255,255,0.35)'
};

const westernFallbackCardStyle: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(19,30,48,0.96) 0%, rgba(12,22,37,0.96) 100%)',
  border: '1px solid rgba(224,122,28,0.55)',
  borderRadius: 8,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  display: 'grid',
  gap: 10,
  padding: '16px 18px'
};

const westernFallbackHeaderStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 12,
  justifyContent: 'space-between'
};

const westernFallbackTitleStyle: CSSProperties = {
  color: '#f1d3a1',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '1rem',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase'
};

const westernFallbackStatusStyle: CSSProperties = {
  border: '1px solid rgba(224,122,28,0.58)',
  borderRadius: 999,
  color: '#f4a340',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.72rem',
  letterSpacing: '0.06em',
  padding: '4px 8px',
  textTransform: 'uppercase'
};

const westernFallbackBodyStyle: CSSProperties = {
  color: 'rgba(231,217,181,0.82)',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.82rem',
  letterSpacing: '0.04em',
  lineHeight: 1.55
};

const westernFallbackMetaStyle: CSSProperties = {
  alignItems: 'center',
  color: 'rgba(231,217,181,0.76)',
  display: 'flex',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.88rem',
  fontWeight: 700,
  justifyContent: 'space-between',
  letterSpacing: '0.08em',
  textTransform: 'uppercase'
};

const westernFinePrintStyle: CSSProperties = {
  color: 'rgba(231,217,181,0.62)',
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  lineHeight: 1.65,
  padding: '6px 4px 0',
  textTransform: 'uppercase'
};

const modalProgressSummaryStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',
  gap: '10px',
  backgroundColor: 'rgba(233, 140, 38, 0.14)',
  border: '1px solid rgba(233, 140, 38, 0.22)',
  borderRadius: 8,
  padding: '12px'
};

const modalRewardSummaryStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '8px',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '12px'
};

const modalMutedStyle: CSSProperties = {
  display: 'block',
  color: 'rgba(255,255,255,0.62)',
  fontSize: '0.78rem',
  marginBottom: 4
};

const modalProgressValueStyle: CSSProperties = {
  color: '#fff',
  fontSize: '1.35rem',
  lineHeight: 1.15
};

// Gold of the $100K monthly promo pill (GamePromoProgressIndicator): metal gradient, light
// gold text on dark surfaces, dark "engraved" ink on the metal itself.
const GOLD_METAL = PROMO_GOLD_METAL;
const GOLD_TEXT = '#ffd97a';
const GOLD_INK = '#3b2008';
const GOLD_INK_SOFT = 'rgba(59, 32, 8, 0.78)';

const goldCardStyle: CSSProperties = {
  backgroundImage: GOLD_METAL,
  backgroundColor: '#d9a63a',
  borderColor: 'rgba(92, 52, 10, 0.85)',
  boxShadow:
    'inset 0 1px 0 rgba(255, 250, 210, 0.85), inset 0 -2px 4px rgba(108, 68, 12, 0.45), 0 0 18px rgba(246, 207, 98, 0.28)',
  position: 'relative'
};

const goldCardValueStyle: CSSProperties = {
  color: GOLD_INK,
  textShadow: '0 1px 0 rgba(255, 235, 170, 0.6)'
};

const goldPillStyle: CSSProperties = {
  backgroundColor: 'rgba(59, 32, 8, 0.55)',
  borderColor: 'rgba(255, 217, 122, 0.7)',
  color: GOLD_TEXT
};

const goldRowStyle: CSSProperties = {
  backgroundColor: 'rgba(255, 217, 122, 0.08)',
  boxShadow: 'inset 0 0 0 1px rgba(255, 217, 122, 0.28)'
};

const tabularStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums'
};

// Hugs its label: the card is a stretch column, so without alignSelf the pill filled the
// row and right-aligned text left a gap on the left (EN-11291).
const statusPillStyle: CSSProperties = {
  alignSelf: 'flex-start',
  backgroundColor: 'rgba(233, 140, 38, 0.16)',
  border: '1px solid rgba(233, 140, 38, 0.45)',
  borderRadius: 999,
  color: '#f4a340',
  fontSize: '0.72rem',
  fontWeight: 700,
  lineHeight: 1.2,
  padding: '4px 10px',
  // One line, never wider than its card: a cash-back status that does not fit switches to its short form,
  // and anything still too long is cut with an ellipsis rather than spilling out.
  position: 'relative',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

// The full status, laid out off-screen at its natural width so the badge can tell whether it fits.
const statusMeasureStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  whiteSpace: 'nowrap'
};

const tierTrackStyle: CSSProperties = {
  width: '100%',
  height: 6,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative'
};

const tierFillStyle: CSSProperties = {
  height: '100%',
  borderRadius: 3,
  background: 'linear-gradient(90deg, #E98C26, #e8b84a)',
  width: '0%'
};

const modalSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const modalSectionTitleStyle: CSSProperties = {
  color: 'rgba(231,217,181,0.78)',
  fontSize: '0.72rem',
  fontWeight: 400,
  textTransform: 'uppercase'
};

// "Total" mirrors the title: flush with the tier cards' right edge, as the title is with the left.
const tierSectionTitleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px'
};

// The section title's letter-spacing trails the last letter; pull it back so the L meets the edge.
const tierValueColumnLabelStyle: CSSProperties = {
  marginInlineEnd: '-0.14em'
};

const modalRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '76px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: '0.85rem',
  fontVariantNumeric: 'tabular-nums'
};

const modalReachedStyle: CSSProperties = {
  color: '#E98C26',
  fontWeight: 800
};

const modalNotReachedStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.46)',
  fontWeight: 700
};

const modalEmptyStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.48)',
  fontSize: '0.72rem'
};

const weekMilestonePanelStyle: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: '12px'
};

const weekMilestoneGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(68px, 1fr))',
  gap: '10px',
  alignItems: 'start'
};

const weekMilestoneStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
  textAlign: 'center',
  border: '1px solid transparent',
  borderRadius: 8,
  padding: '8px 2px'
};

const weekCurrentStyle: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.06)'
};

const weekUpcomingIconStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.16)',
  fontSize: '1.7rem'
};

const weekUpcomingAmountStyle: CSSProperties = {
  ...modalNotReachedStyle,
  color: 'rgba(255,255,255,0.32)'
};

const weekRangeStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.55)',
  fontSize: '0.7rem',
  whiteSpace: 'nowrap'
};

const weekMilestoneLabelStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.82)',
  fontSize: '0.78rem',
  fontWeight: 800,
  whiteSpace: 'nowrap'
};

const weekReachedIconStyle: CSSProperties = {
  color: '#E98C26',
  fontSize: '1.7rem'
};

const weekOpenIconStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.24)',
  fontSize: '1.7rem'
};

const weekMilestoneHintStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.62)',
  fontSize: '0.78rem',
  lineHeight: 1.45,
  textWrap: 'pretty' as CSSProperties['textWrap'],
  marginTop: '10px',
  textAlign: 'center'
};

const detailGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '8px'
};

const detailItemStyle: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderRadius: 8,
  color: 'rgba(255,255,255,0.62)',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.72rem',
  gap: '4px',
  lineHeight: 1.35,
  padding: '10px 12px'
};

const detailValueStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.92)',
  fontSize: '0.8rem',
  fontWeight: 600
};

/* Fine print: no cards, no fills; one hairline and smaller, quieter type than the rewards
   above it, so it reads as terms rather than as part of the reward. */
const finePrintStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  paddingTop: '14px',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  color: 'rgba(255,255,255,0.55)',
  fontSize: '0.72rem',
  lineHeight: 1.45
};

const finePrintListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  margin: 0
};

const finePrintRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '2px 12px'
};

const finePrintValueStyle: CSSProperties = {
  margin: 0,
  color: 'rgba(255,255,255,0.78)',
  fontVariantNumeric: 'tabular-nums'
};

const finePrintTextStyle: CSSProperties = {
  margin: 0,
  textWrap: 'pretty' as CSSProperties['textWrap']
};

const finePrintLinkStyle: CSSProperties = {
  color: 'inherit',
  textDecorationThickness: 'from-font',
  textUnderlineOffset: '0.15em'
};

// The terms address in the fine print opens the page it names (copy comes from Admin).
const FINE_PRINT_LINK = /\b(hijackpoker\.com(?:\/[\w-]+)*)/;

const linkifyFinePrint = (text: string): ReactNode => {
  const match = FINE_PRINT_LINK.exec(text);
  if (!match) return text;
  const [address] = match;
  return (
    <>
      {text.slice(0, match.index)}
      <a
        href={`https://${address}`}
        target='_blank'
        rel='noopener noreferrer'
        style={finePrintLinkStyle}>
        {address}
      </a>
      {text.slice(match.index + address.length)}
    </>
  );
};

const endDateStyle: CSSProperties = {
  fontSize: '0.72rem',
  color: 'rgba(255, 255, 255, 0.55)',
  textAlign: 'right'
};
